import CircleWithTickIcon from "@/assets/colorable/circle_with_tick.svg?react";
import InfinityIcon from "@/assets/colorable/infinity.svg?react";
import InfoIcon from "@/assets/colorable/info.svg?react";
import type {PackageSubscriptionSerializer} from "@/generated/DjangoTypes";
import {usePlanPerks} from "@/hooks/usePlanPerks";
import clsxm from "@/lib/clsxm";
import useUserStore from "@/store/user";
import {
  getFeatureTranslationKey,
  isInfinite,
  isPrefixedWithValue,
  TIKTOK_FREQUENCY_BY_HOURS,
  type TPlanFeatureKey,
} from "@/utils/planUtils";
import map from "lodash/map";
import {type FC} from "react";
import {Trans, useTranslation} from "react-i18next";
import {Tooltip} from "react-tooltip";

const UnlockedFeaturesList: FC<{
  addOnsClassName?: string;
  addOnsListClassName?: string;
  className?: string;
  iconClassName?: string;
  id?: string;
  subscription?: PackageSubscriptionSerializer | null;
}> = ({addOnsClassName, addOnsListClassName, className, iconClassName, id, subscription: subscriptionFromProps}) => {
  const {t} = useTranslation(["common", "plans"]);
  const subscriptionFromStore = useUserStore(state => state.user.package_subscription);

  const subscription = subscriptionFromProps ?? subscriptionFromStore;
  const {unlockedAddOnFeatures, unlockedFeatures} = usePlanPerks(subscription);
  const hasTiktokSearch = !!subscription?.tiktok_search;
  const hasCampaignBooster = !!subscription?.campaign_booster;

  const keywordLimit = unlockedAddOnFeatures.tiktok_visibility_keyword_limit;
  const countryCount = unlockedAddOnFeatures.tiktok_visibility_country_count;
  const syncIntervalHours = unlockedAddOnFeatures.tiktok_visibility_sync_interval_hours;

  const isKeywordLimitInfinite =
    typeof keywordLimit === "number" && isInfinite(keywordLimit, "tiktok_visibility_keyword_limit");
  const frequencyKey =
    typeof syncIntervalHours === "number"
      ? (TIKTOK_FREQUENCY_BY_HOURS[syncIntervalHours]?.word as "tiktok_visibility_frequency_daily")
      : undefined;

  return (
    <div id={id} className={clsxm("flex flex-wrap gap-5 gap-y-3", className)}>
      {map(unlockedFeatures, (rawValue, key) => {
        const feature = {
          key: key as TPlanFeatureKey,
          value: typeof rawValue === "string" ? null : rawValue,
        };

        return (
          <div key={key} className="flex w-fit flex-row items-center gap-1.5">
            <CircleWithTickIcon className={clsxm("text-success-herbal mr-1.5 size-6 shrink-0", iconClassName)} />
            {isInfinite(feature.value, feature.key) && <InfinityIcon className="w-5" />}
            {isPrefixedWithValue(feature) && <span className="text-neutral-black font-semibold">{feature.value}</span>}
            <span className="text-neutral-black font-semibold sm:whitespace-nowrap">
              {t(getFeatureTranslationKey(feature) as "influencers_per_campaign", {
                count: typeof feature.value === "number" ? feature.value : undefined,
                value: feature.value,
              })}
            </span>
          </div>
        );
      })}
      {(hasTiktokSearch || hasCampaignBooster) && (
        <div
          className={clsxm("border-neutral-grey-light-dark flex w-full flex-col gap-2 border-t pt-3", addOnsClassName)}>
          <span className="text-neutral-grey-dark text-sm font-medium">{t("plans:unlocked_add_ons")}</span>
          <div className={clsxm("flex flex-wrap gap-5 gap-y-3", addOnsListClassName)}>
            {hasTiktokSearch && (
              <div className="flex w-fit flex-row items-center gap-1.5 text-left">
                <CircleWithTickIcon className={clsxm("text-success-herbal mr-1.5 size-6 shrink-0", iconClassName)} />
                <span className="text-neutral-black font-semibold">
                  <Trans
                    t={t}
                    i18nKey="tiktok_search_visibility_perk"
                    values={{
                      countries: t("tiktok_search_countries", {
                        count: typeof countryCount === "number" ? countryCount : 1,
                      }),
                      frequency: frequencyKey ? t(frequencyKey) : "",
                    }}
                    components={[
                      <br className="sm:hidden" />,
                      <span className="mr-1 mb-0.5 inline-block shrink-0 align-middle">
                        {isKeywordLimitInfinite ? (
                          <InfinityIcon className="w-5" />
                        ) : typeof keywordLimit === "number" ? (
                          keywordLimit
                        ) : null}
                      </span>,
                    ]}
                  />
                </span>
                <InfoIcon
                  className="text-primary-dark-solid size-4 shrink-0 cursor-pointer"
                  data-tooltip-id="unlocked-add-on-info"
                  data-tooltip-content={t("plans:add_on_social_search_info")}
                />
              </div>
            )}
            {hasCampaignBooster && (
              <div className="flex w-fit flex-row items-center gap-1.5">
                <CircleWithTickIcon className={clsxm("text-success-herbal mr-1.5 size-6 shrink-0", iconClassName)} />
                <span className="text-neutral-black font-semibold sm:whitespace-nowrap">
                  {t("plans:add_on_booster_title")}
                </span>
                <InfoIcon
                  className="text-primary-dark-solid size-4 shrink-0 cursor-pointer"
                  data-tooltip-id="unlocked-add-on-info"
                  data-tooltip-content={t("plans:add_on_booster_info")}
                />
              </div>
            )}
            <Tooltip id="unlocked-add-on-info" place="top" variant="dark" className="z-100 max-w-64 text-xs" />
          </div>
        </div>
      )}
    </div>
  );
};

export default UnlockedFeaturesList;
